import { GANTT_SOURCE_MANUAL } from "../../domainCatalog";

export const currentIsoDate = () => localIsoDate(new Date());

export const emptyGantt = () => {
  const today = currentIsoDate();
  return {
    title: "Project Gantt",
    start_date: today,
    end_date: addDays(today, 84),
    items: [],
    risks: [],
    deadlines: [],
    source_type: GANTT_SOURCE_MANUAL
  };
};

export const editable = (data) => ({
  title: data.title,
  start_date: data.start_date,
  end_date: data.end_date,
  items: data.items || [],
  risks: data.risks || [],
  deadlines: data.deadlines || [],
  source_type: data.source_type || GANTT_SOURCE_MANUAL
});

export const draftOnly = ({ title, start_date, end_date, items, risks }) => ({
  title,
  start_date,
  end_date,
  items,
  risks
});

export function addDays(value, days) {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export function localIsoDate(date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export const daysBetween = (start, end) => Math.round(
  (new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000
);

export const shortDate = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(
  undefined,
  { month: "short", day: "numeric" }
);

export const formatUsDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${month}/${day}/${year}` : "";
};

export const parseUsDate = (value) => {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, month, day, year] = match;
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00`);
  return date.getFullYear() === Number(year)
    && date.getMonth() + 1 === Number(month)
    && date.getDate() === Number(day)
    ? iso
    : null;
};

export const datePercent = (value, start, end) => Math.max(
  0,
  Math.min(100, (daysBetween(start, value) / Math.max(daysBetween(start, end), 1)) * 100)
);

export const barStyle = (item, start, end) => ({
  left: `${datePercent(item.start_date, start, end)}%`,
  width: `${Math.max(0.6, datePercent(item.end_date, start, end) - datePercent(item.start_date, start, end))}%`
});

export const dateFromPointer = (event, bounds, start, end) => {
  const clientX = Number(event.clientX);
  if (!start || !end || !Number.isFinite(clientX) || !bounds?.width) return null;
  const ratio = Math.max(0, Math.min(1, (clientX - bounds.left) / Math.max(bounds.width, 1)));
  return addDays(start, Math.round(ratio * Math.max(daysBetween(start, end), 0)));
};

export const estimatedEndDate = (item, effortHours, team) => {
  const assigned = new Set(item.assignee_ids || []);
  const weeklyHours = team.reduce(
    (total, member) => assigned.has(member.id)
      ? total + Math.max(Number(member.availability_hours_per_week) || 0, 0)
      : total,
    0
  );
  if (!weeklyHours) return item.end_date;
  const dailyHours = Math.max(weeklyHours, 1) / 5;
  const reviewDays = Math.max(1, Math.ceil(Math.max(Number(effortHours) || 0, 0) / dailyHours));
  let reviewStart = onOrAfterWorkday(item.start_date);
  const agentHours = Math.max(Number(item.agent_execution_hours) || 0, 0);
  if (agentHours > 0) {
    const agentDays = Math.max(1, Math.ceil(agentHours / 8));
    reviewStart = nextWorkday(addWorkdays(reviewStart, agentDays - 1));
  }
  return addWorkdays(reviewStart, reviewDays - 1);
};

export const estimatedEffortHoursFromDates = (item, team) => {
  const assigned = new Set(item.assignee_ids || []);
  const weeklyHours = team.reduce(
    (total, member) => assigned.has(member.id)
      ? total + Math.max(Number(member.availability_hours_per_week) || 0, 0)
      : total,
    0
  );
  if (!weeklyHours) return item.effort_hours;
  return Math.round(countWorkdays(item.start_date, item.end_date) * (weeklyHours / 5));
};

export const reflowFromEffortChange = (items, changedId, effortHours, team) => {
  const changedIndex = items.findIndex((item) => item.id === changedId);
  if (changedIndex < 0) return items;
  const memberReady = new Map();
  const memberShift = new Map();
  const nextItems = items.slice(0, changedIndex).map((item) => {
    updateMemberReady(memberReady, item);
    return item;
  });

  items.slice(changedIndex).forEach((original, offset) => {
    const assigned = original.assignee_ids || [];
    if (!offset) {
      const revised = { ...original, effort_hours: effortHours };
      revised.end_date = estimatedEndDate(revised, effortHours, team);
      assigned.forEach((id) => memberShift.set(id, daysBetween(original.end_date, revised.end_date)));
      updateMemberReady(memberReady, revised);
      nextItems.push(revised);
      return;
    }

    const shifts = assigned.filter((id) => memberShift.has(id)).map((id) => memberShift.get(id));
    if (!shifts.length) {
      updateMemberReady(memberReady, original);
      nextItems.push(original);
      return;
    }
    const shiftedStart = onOrAfterWorkday(addDays(original.start_date, Math.max(...shifts)));
    const resourceReady = assigned
      .map((id) => memberReady.get(id))
      .filter(Boolean)
      .reduce((latest, value) => value > latest ? value : latest, shiftedStart);
    const revised = { ...original, start_date: resourceReady > shiftedStart ? resourceReady : shiftedStart };
    revised.end_date = estimatedEndDate(revised, revised.effort_hours, team);
    const endShift = daysBetween(original.end_date, revised.end_date);
    assigned.forEach((id) => memberShift.set(id, endShift));
    updateMemberReady(memberReady, revised);
    nextItems.push(revised);
  });
  return nextItems;
};

const updateMemberReady = (memberReady, item) => (item.assignee_ids || []).forEach((id) => {
  const ready = nextWorkday(item.end_date);
  if (!memberReady.get(id) || ready > memberReady.get(id)) memberReady.set(id, ready);
});

export const onOrAfterWorkday = (value) => {
  let result = value;
  while ([0, 6].includes(new Date(`${result}T00:00:00`).getDay())) result = addDays(result, 1);
  return result;
};

export const nextWorkday = (value) => onOrAfterWorkday(addDays(value, 1));

export const addWorkdays = (value, days) => {
  let result = value;
  let remaining = Math.max(days, 0);
  while (remaining) {
    result = addDays(result, 1);
    if (![0, 6].includes(new Date(`${result}T00:00:00`).getDay())) remaining -= 1;
  }
  return result;
};

export const countWorkdays = (start, end) => {
  let result = 0;
  for (let date = start; date <= end; date = addDays(date, 1)) {
    if (![0, 6].includes(new Date(`${date}T00:00:00`).getDay())) result += 1;
  }
  return result;
};

export const itemStatus = (item, currentDate, projectDueDate) => item.progress >= 100
  ? "done"
  : currentDate > item.end_date
    ? "late"
    : item.delivery_risk?.trim()
      || item.end_date > projectDueDate
      || (item.start_date <= currentDate && currentDate <= item.end_date && item.risk?.trim())
      ? "at-risk"
      : "on-track";

export const statusLabel = (status) => ({
  "on-track": "On track",
  "at-risk": "At risk",
  done: "Done",
  late: "Late"
}[status]);

export const jobStatusMessage = (job, label) => {
  if (job.status === "completed") return `${label} completed.`;
  if (job.status === "failed") return job.error_message || `${label} failed.`;
  return `${label}: ${job.current_step || "Queued"} (${job.progress || 0}%).`;
};

export const assigneeLabel = (item, team) => {
  const names = (item.assignee_ids || [])
    .map((id) => team.find((member) => member.id === id)?.name)
    .filter(Boolean);
  return names.length ? names.join(", ") : "Unassigned";
};

export const initials = (name = "") => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase() || "?";

export const buildWeeks = (gantt, today) => {
  const first = gantt.start_date < today ? gantt.start_date : today;
  const lastItem = gantt.items.reduce(
    (latest, item) => item.end_date > latest ? item.end_date : latest,
    gantt.end_date
  );
  const last = lastItem > today ? lastItem : today;
  const count = Math.max(1, Math.ceil((daysBetween(first, last) + 1) / 7));
  return Array.from({ length: count }, (_, index) => addDays(first, index * 7));
};
